import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router'
import { FileText } from 'lucide-react'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Select,
  Textarea,
  useAuth,
  useCan,
  useToast,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import type { CompetitionDetail } from '../../lib/competition-types'
import { FORMS_KEY, type FormDetail, type FormSummary } from '../../lib/form-types'
import { COMPETITIONS_KEY } from './CompetitionsPage'

const CONTROLLED = ['open', 'suspended', 'closed']

/**
 * Formulaire de candidature du concours : une version PUBLIÉE (immuable).
 * Après l'ouverture, en changer est une procédure contrôlée (forms.publish +
 * motif) ; les dossiers déjà créés gardent leur version.
 */
export function FormTab({ c }: { c: CompetitionDetail }) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canList = useCan('forms.view')
  const [formId, setFormId] = useState(c.form?.formId ?? '')
  const [versionId, setVersionId] = useState(c.form?.versionId ?? '')
  const [reason, setReason] = useState('')
  const controlled = CONTROLLED.includes(c.status)

  const forms = useQuery({
    queryKey: FORMS_KEY,
    queryFn: () => session.api.get<{ data: FormSummary[] }>('/admin/forms').then((r) => r.data),
    enabled: canList && c.can.changeForm,
  })
  const selected = useQuery({
    queryKey: [...FORMS_KEY, formId],
    queryFn: () => session.api.get<FormDetail>(`/admin/forms/${formId}`),
    enabled: Boolean(formId) && canList && c.can.changeForm,
  })
  const published = (selected.data?.versions ?? []).filter((v) => v.status === 'published')

  const link = useMutation({
    mutationFn: () =>
      session.api.put<CompetitionDetail>(`/admin/competitions/${c.id}/form`, {
        formVersionId: versionId || null,
        reason: reason || null,
      }),
    onSuccess: async (updated) => {
      queryClient.setQueryData([...COMPETITIONS_KEY, c.id], updated)
      toast.success(
        'Formulaire associé',
        updated.form ? `${updated.form.title} — v${updated.form.version}` : undefined
      )
      setReason('')
      await queryClient.invalidateQueries({ queryKey: COMPETITIONS_KEY })
    },
    onError: (error) => {
      if (!Object.keys(fieldErrors(error)).length) toast.error(errorMessage(error))
    },
  })
  const errors = fieldErrors(link.error)
  const changed = versionId !== (c.form?.versionId ?? '')

  return (
    <Card>
      <CardHeader title="Formulaire de candidature" />
      <CardBody>
        <div className="stack" style={{ gap: 'var(--space-4)' }}>
          {c.form ? (
            <p style={{ margin: 0 }}>
              <FileText aria-hidden="true" size={16} style={{ verticalAlign: '-3px' }} />{' '}
              <Link to={`/formulaires/${c.form.formId}`}>{c.form.title}</Link>{' '}
              <Badge variant="success">v{c.form.version}</Badge>{' '}
              <span className="perm-code" style={{ display: 'inline' }}>
                {c.form.code}
              </span>
            </p>
          ) : (
            <Alert tone="warning">
              Aucun formulaire associé : les dépôts ne peuvent pas ouvrir.
            </Alert>
          )}
          {controlled && (
            <Alert tone="info" title="Procédure contrôlée">
              Les dépôts sont ouverts : changer de version exige la permission de publier les
              formulaires et un motif. Les dossiers déjà créés conservent leur version.
            </Alert>
          )}
          {!c.can.changeForm ? (
            <p className="text-muted">Vous ne pouvez pas changer le formulaire de ce concours.</p>
          ) : !canList ? (
            <p className="text-muted">
              La liste des formulaires nécessite la permission de les consulter.
            </p>
          ) : (
            <>
              <div className="form-grid">
                <Select
                  label="Formulaire"
                  value={formId}
                  placeholder="— Choisir —"
                  options={(forms.data ?? []).map((f) => ({
                    value: f.id,
                    label: `${f.title} (${f.code})`,
                  }))}
                  onChange={(e) => {
                    setFormId(e.target.value)
                    setVersionId('')
                  }}
                />
                <Select
                  label="Version publiée"
                  value={versionId}
                  placeholder={
                    formId && !published.length ? 'Aucune version publiée' : '— Choisir —'
                  }
                  options={published.map((v) => ({
                    value: v.id,
                    label: `v${v.version} — ${v.fields} champs`,
                  }))}
                  onChange={(e) => setVersionId(e.target.value)}
                  error={errors.formVersionId}
                />
              </div>
              {controlled && (
                <Textarea
                  label="Motif"
                  required
                  rows={2}
                  hint="10 caractères minimum. Conservé dans l’historique du concours et le journal d’audit."
                  value={reason}
                  error={errors.reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              )}
              <div>
                <Button
                  variant="primary"
                  loading={link.isPending}
                  disabled={!changed || !versionId || (controlled && reason.trim().length < 10)}
                  onClick={() => link.mutate()}
                >
                  Associer cette version
                </Button>
              </div>
            </>
          )}
        </div>
      </CardBody>
    </Card>
  )
}
